import { GenericCardTitle } from '@/components/GenericCard/GenericCardTitle'
import { CardLabel } from '@/components/row_renderers/utils/CardFooter'
import { Box, Stack } from '@mui/material'
import { FileHandleAssociation } from '@sage-bionetworks/synapse-types'
import React, { CSSProperties, forwardRef } from 'react'
import { DescriptionConfig } from '../CardContainerLogic'
import HeaderCard, { HeaderCardVariant } from '../HeaderCard'
import { CardFooter } from '../row_renderers/utils'
import { SmartLink } from '../SmartLink/SmartLink'
import { SustainabilityScorecardProps } from '../SustainabilityScorecard/SustainabilityScorecard'
import { FileHandleLink } from '../widgets/FileHandleLink'
import { CollapsibleDescription } from './CollapsibleDescription'

/** Resolved CTA link configuration with actual href values (as opposed to CTACardLink which uses column names) */
export type CTALinkConfig = {
  text: React.ReactNode
  href?: string
  target?: string
}

export type GenericCardProps = {
  /** String representing the 'type' of object. This is displayed as a label on the card. */
  type: string
  /** The title displayed on the card. */
  title: string
  /** Optionally provide href/target if the title should be a link */
  titleLinkConfiguration?: {
    href: string
    target: string
  }
  /** Optionally provide configuration if the title should be a link to a Synapse FileHandle */
  titleAsFileHandleLinkConfiguration?: {
    /** The FileHandleAssociation used to get access to the file handle */
    fileHandleAssociation: FileHandleAssociation
    /** Whether a 'download' icon should be shown */
    showDownloadIcon: boolean
  }
  /** An optional subtitle to be displayed on the card */
  subtitle?: string
  /** An description to be displayed on the card */
  description: string
  /** An optional description subtitle to be displayed on the card */
  descriptionSubTitle?: string
  /** Configuration for altering the display of the description prop */
  descriptionConfig?: DescriptionConfig
  /** Optional slot for adding content to the top of the card */
  cardTopContent?: React.ReactNode
  /** Optional slot for adding action buttons to the top of the card */
  cardTopButtons?: React.ReactNode
  /** If true, a HeaderCard component will be rendered */
  isHeader?: boolean
  /** The variant of HeaderCard to render if `isHeader` is true */
  headerCardVariant?: HeaderCardVariant
  /** Set to true if the icon is an arbitrary image. The card styles will be updated to accommodate the image
   * @default false */
  useStylesForDisplayedImage?: boolean
  /**
   * The rendered icon on the card
   */
  icon: React.ReactNode
  /**
   * The card labels to be displayed in the footer of the card
   */
  labels?: CardLabel[]
  /**
   * The initial number of labels to display in the footer of the card
   */
  secondaryLabelLimit?: number
  /**
   * Optional configuration for displaying CTA button(s) on the card. Accepts a single config or an array.
   */
  ctaLinkConfig?: CTALinkConfig | CTALinkConfig[]
  /**
   * The rendered icon list on the card
   */
  renderedIconList?: React.ReactNode
  /**
   * Optional sustainability scorecard to be displayed on the header card
   */
  sustainabilityScorecard?: SustainabilityScorecardProps
  /**
   * Optional ReactNode to be rendered next to the card type
   */
  cardTypeAdornment?: React.ReactNode
  /**
   * Optional content to render to the right of the title/subtitle/description area.
   */
  titleAreaRightContent?: React.ReactNode
  /**
   * Character count threshold for truncating description
   * @default 400
   */
  charCountCutoff?: number
}

const EMPTY_CARD_LABEL_ARRAY: CardLabel[] = []

/**
 * Generic portal card UI component with a predefined layout
 */
export const GenericCard = forwardRef(function GenericCard(
  props: GenericCardProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const {
    icon,
    type,
    title,
    titleLinkConfiguration,
    titleAsFileHandleLinkConfiguration,
    subtitle,
    description,
    descriptionSubTitle = '',
    descriptionConfig,
    cardTopContent,
    cardTopButtons,
    isHeader = false,
    headerCardVariant,
    useStylesForDisplayedImage = false,
    labels = EMPTY_CARD_LABEL_ARRAY,
    secondaryLabelLimit,
    ctaLinkConfig,
    renderedIconList,
    sustainabilityScorecard,
    cardTypeAdornment,
    titleAreaRightContent,
    charCountCutoff,
  } = props

  const showFooter = labels.length > 0

  const style: CSSProperties = {
    // undefined, take default value from class
    marginTop: isHeader ? '0px' : undefined,
    marginBottom: isHeader ? '0px' : undefined,
    paddingBottom:
      showFooter || useStylesForDisplayedImage ? undefined : '15px',
  }

  if (isHeader) {
    return (
      <HeaderCard
        ref={ref}
        headerCardVariant={headerCardVariant}
        descriptionConfig={descriptionConfig}
        charCountCutoff={charCountCutoff}
        title={title}
        subTitle={subtitle}
        description={description}
        type={type}
        icon={icon}
        values={labels}
        href={titleLinkConfiguration?.href}
        target={titleLinkConfiguration?.target}
        ctaLinkConfig={ctaLinkConfig}
        isAlignToLeftNav={true}
        secondaryLabelLimit={secondaryLabelLimit}
        cardTopButtons={cardTopButtons}
        cardTopContent={cardTopContent}
        sustainabilityScorecard={sustainabilityScorecard}
      />
    )
  }

  return (
    <div style={style} ref={ref} className={'SRC-portalCard'}>
      <div className={'SRC-portalCardMain'}>
        {icon}
        <div className="SRC-cardContent">
          {cardTopButtons && (
            <Box
              sx={{
                position: 'absolute',
                right: '24px',
                display: 'flex',
                float: 'right',
                flexDirection: 'row',
                gap: '10px',
              }}
            >
              {cardTopButtons}
            </Box>
          )}
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <div className="SRC-type">{type}</div>
            {cardTypeAdornment}
          </Stack>
          {renderedIconList}
          <Box className="SRC-cardTitleArea">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <div>
                <h3
                  className="SRC-boldText SRC-blackText"
                  style={{ margin: 'none' }}
                >
                  {!titleAsFileHandleLinkConfiguration && (
                    <GenericCardTitle
                      title={title}
                      href={titleLinkConfiguration?.href}
                      target={titleLinkConfiguration?.target}
                    />
                  )}
                  {titleAsFileHandleLinkConfiguration && (
                    <FileHandleLink
                      fileHandleAssociation={
                        titleAsFileHandleLinkConfiguration.fileHandleAssociation
                      }
                      showDownloadIcon={
                        titleAsFileHandleLinkConfiguration.showDownloadIcon
                      }
                      displayValue={title}
                    />
                  )}
                </h3>
              </div>
              {subtitle && <div className="SRC-author">{subtitle}</div>}
              <CollapsibleDescription
                description={description}
                descriptionSubTitle={descriptionSubTitle}
                descriptionConfig={descriptionConfig}
              />
              {ctaLinkConfig && (
                <Box
                  sx={{ mt: '20px', display: 'flex', gap: 2, flexWrap: 'wrap' }}
                >
                  {(Array.isArray(ctaLinkConfig)
                    ? ctaLinkConfig
                    : [ctaLinkConfig]
                  ).map(
                    (config, index) =>
                      config.text &&
                      config.href && (
                        <SmartLink
                          key={index}
                          href={config.href}
                          target={config.target}
                        >
                          {config.text}
                        </SmartLink>
                      ),
                  )}
                </Box>
              )}
            </Box>
            {titleAreaRightContent && (
              <div className="SRC-cardTitleAreaDetails">
                {titleAreaRightContent}
              </div>
            )}
          </Box>
        </div>
      </div>
      {showFooter && (
        <CardFooter
          isHeader={false}
          secondaryLabelLimit={secondaryLabelLimit}
          values={labels}
          className={useStylesForDisplayedImage ? undefined : 'hasIcon'}
          cardTopContent={cardTopContent}
        />
      )}
    </div>
  )
})

export default GenericCard
